import React, { useState, Children } from 'react';
import { TopNav } from '../components/layout/TopNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { TrustBadge } from '../components/ui/TrustBadge';
import { ChevronDown, Mail, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function WICEBTGuidePage() {
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-387">
      <EBTWICBanner data-id="element-388" />
      <TopNav data-id="element-389" />

      <main className="max-w-4xl mx-auto px-6 py-16" data-id="element-390">
        <div className="text-center mb-16" data-id="element-391">
          <h1 className="text-5xl font-bold text-gray-900 mb-6" data-id="element-392">
            WIC & EBT Guide
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-393">
            We're committed to making healthy, culturally appropriate food
            accessible to everyone in the Hyattsville community.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-16" data-id="element-394">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-395">
            <TrustBadge type="WIC" className="mb-4" size="lg" data-id="element-396" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-397">
              WIC Program
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-398">
              Women, Infants, and Children nutrition program approved items. We
              carry a wide selection of WIC-eligible products including baby
              formula, fresh produce, and whole grains.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-399">
            <TrustBadge type="EBT" className="mb-4" size="lg" data-id="element-400" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-401">
              SNAP / EBT
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-402">
              Supplemental Nutrition Assistance Program payments accepted. Shop
              with confidence knowing most of our products are EBT-eligible.
            </p>
          </div>
        </div>

        <section className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100 mb-16" data-id="element-403">
          <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-404">
            How to Shop with Benefits
          </h2>

          <div className="space-y-8" data-id="element-405">
            <div className="flex gap-6" data-id="element-406">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D97757] to-[#F4A261] text-white flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-md" data-id="element-407">
                1
              </div>
              <div data-id="element-408">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-409">
                  Look for the Badges
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-410">
                  Items eligible for WIC or EBT are clearly marked with badges
                  on the product card. This makes it easy to identify what you
                  can purchase with your benefits.
                </p>
              </div>
            </div>

            <div className="flex gap-6" data-id="element-411">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D97757] to-[#F4A261] text-white flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-md" data-id="element-412">
                2
              </div>
              <div data-id="element-413">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-414">
                  Filter Your Search
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-415">
                  Use the "EBT Eligible Only" toggle in the search page to see
                  only items you can buy with your benefits. This helps you shop
                  faster and with confidence.
                </p>
              </div>
            </div>

            <div className="flex gap-6" data-id="element-416">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D97757] to-[#F4A261] text-white flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-md" data-id="element-417">
                3
              </div>
              <div data-id="element-418">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-419">
                  Checkout Securely
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-420">
                  At checkout, select "EBT/SNAP" as your payment method. You'll
                  enter your PIN securely, and we'll process your benefits
                  payment immediately.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16" data-id="element-421">
          <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-422">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4" data-id="element-423">
            <FAQItem question="Can I use WIC for online orders?" answer="Yes! Motojesi International Market is one of the few approved online retailers for WIC in Maryland. You can order online and pay with your WIC card at delivery or pickup." data-id="element-424" />
            <FAQItem question="What if an item is out of stock?" answer="Our system will suggest EBT-eligible substitutions. We will never substitute a WIC item without your explicit approval." data-id="element-425" />
            <FAQItem question="Is there a delivery fee for EBT orders?" answer="Orders over $35 qualify for free delivery, regardless of payment method. For smaller orders, the standard $5.99 fee applies." data-id="element-426" />
            <FAQItem question="How do I know if a product is WIC or EBT eligible?" answer="Every product page displays clear WIC and EBT badges if the item is eligible. You can also use our filter to show only eligible items." data-id="element-427" />
          </div>
        </section>

        <section className="bg-gradient-to-br from-[#1E3A5F] to-[#2D5F3F] text-white p-10 rounded-2xl shadow-lg" data-id="element-428">
          <h2 className="text-3xl font-bold mb-6" data-id="element-429">Need Help?</h2>
          <p className="text-blue-100 mb-8 text-lg" data-id="element-430">
            Our team is here to assist you with any questions about using your
            benefits.
          </p>

          <div className="space-y-5" data-id="element-431">
            <a href="mailto:help@motojesimarket.com" className="flex items-center gap-4 text-lg hover:text-[#F4A261] transition-colors group" data-id="element-432">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-colors" data-id="element-433">
                <Mail className="w-6 h-6" data-id="element-434" />
              </div>
              <span className="font-semibold" data-id="element-435">help@motojesimarket.com</span>
            </a>

            <a href="https://www.fns.usda.gov/wic" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-lg hover:text-[#F4A261] transition-colors group" data-id="element-436">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-colors" data-id="element-437">
                <ExternalLink className="w-6 h-6" data-id="element-438" />
              </div>
              <span className="font-semibold" data-id="element-439">Visit Maryland WIC Website</span>
            </a>
          </div>
        </section>
      </main>
    </div>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm" data-id="element-440">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors" data-id="element-441">
        <span className="font-bold text-gray-900 text-lg pr-4" data-id="element-442">{question}</span>
        <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-443" />
      </button>

      <AnimatePresence data-id="element-444">
        {isOpen && <motion.div initial={{
        height: 0
      }} animate={{
        height: 'auto'
      }} exit={{
        height: 0
      }} className="overflow-hidden" data-id="element-445">
            <div className="px-6 pb-6 text-gray-600 leading-relaxed border-t border-gray-50" data-id="element-446">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}